
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BusinessSubscriptionInfo {
  hasActiveSubscription: boolean;
  subscriptionEndDate: string | null;
  subscriptionStatus: string | null;
  planName: string | null;
  isInGracePeriod: boolean;
  cancelAtPeriodEnd: boolean;
}

export function useBusinessSubscriptionStatus(businessId: string) {
  return useQuery({
    queryKey: ['business-subscription-status', businessId],
    queryFn: async (): Promise<BusinessSubscriptionInfo> => {
      // Get the latest subscription for this business
      const { data: subscriptionData, error } = await supabase
        .from('business_subscriptions')
        .select(`
          status,
          current_period_end,
          cancel_at_period_end,
          plan_id,
          community_subscription_plans(name)
        `)
        .eq('business_id', businessId)
        .order('event_timestamp', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!subscriptionData) {
        return {
          hasActiveSubscription: false,
          subscriptionEndDate: null,
          subscriptionStatus: null,
          planName: null,
          isInGracePeriod: false,
          cancelAtPeriodEnd: false
        };
      }

      const now = new Date();
      const periodEnd = new Date(subscriptionData.current_period_end);
      
      // Check if subscription is active based on status and period end
      // Consider canceled subscriptions as active if they're still in their grace period
      const hasActiveSubscription = (
        ['active', 'trialing', 'past_due'].includes(subscriptionData.status) ||
        (periodEnd > now && !['unpaid', 'incomplete_expired'].includes(subscriptionData.status))
      );

      // Determine if we're in a grace period (canceled but still active until period end)
      const isInGracePeriod = subscriptionData.status === 'canceled' && periodEnd > now;

      return {
        hasActiveSubscription,
        subscriptionEndDate: subscriptionData.current_period_end,
        subscriptionStatus: subscriptionData.status,
        planName: subscriptionData.community_subscription_plans?.name || null,
        isInGracePeriod,
        cancelAtPeriodEnd: subscriptionData.cancel_at_period_end || false
      };
    },
    enabled: !!businessId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000 // 1 minute
  });
}
